/* eslint-disable quote-props */
import React, { useEffect, useRef, useState } from 'react';
import { Box, IconButton, Paper, Stack, Tab, Tabs, Tooltip, Typography } from '@mui/material';
import SercomGrid from '../../components/Grid';
import { DataManager } from '@syncfusion/ej2-data';
import { GridComponent, RecordClickEventArgs, SortSettingsModel } from '@syncfusion/ej2-react-grids';
import SercomEnums from '../../support/sercom-enums';
import { usePopupManager } from 'react-popup-manager';
import { useTranslation } from 'react-i18next';
import AdminService from '../../services/admin-service';
import SercomConstants from '../../support/sercom-constants';
import { AccountBalanceWalletOutlined, CalendarMonthOutlined, DeleteOutline, EditOutlined, GroupsOutlined, PaidOutlined, LinkOffOutlined, TimerOutlined } from '@mui/icons-material';
import moment from 'moment';
import SercomMenu from '../../components/SercomMenu';
import { SercomTypes } from '../../support/sercom-types';
import SubscriptionPopup from './SubscriptionPopup';
import snackbarHelper from '../../helpers/snackbar-helper';
import SercomHelper from '../../support/sercom-helper';
import ClearQuickBooksLinksPopup from './ClearQuickBooksLinksPopup';
import CompanyTimersPopup from './CompanyTimersPopup';
import { useSearchParams } from 'react-router-dom';

type CompanyTab = 'clients' | 'trials' | 'internal';
const companyTabs: { value: CompanyTab; label: string }[] = [
  { value: 'clients', label: 'Clients' },
  { value: 'trials', label: 'Trials' },
  { value: 'internal', label: 'Internal' },
];
const gridCustomizationTypes: Record<CompanyTab, SercomEnums.GridCustomizationType> = {
  // Preserve the existing Companies layout as the Clients layout.
  clients: SercomEnums.GridCustomizationType.Company,
  trials: SercomEnums.GridCustomizationType.CompanyTrial,
  internal: SercomEnums.GridCustomizationType.CompanyInternal,
};

const CompaniesPage = () => {
  const [dataSource, setDataSource] = useState<DataManager | undefined>(undefined);
  const [searchParams, setSearchParams] = useSearchParams();
  const companyGridRef = useRef<GridComponent | null>(null);
  const [kpisData, setKpisData] = useState<SercomTypes.KpisData | null>(null);
  const popupManager = usePopupManager();
  const { t } = useTranslation();
  const requestedTab = searchParams.get('view');
  const activeTab: CompanyTab = companyTabs.some((tab) => tab.value === requestedTab) ? requestedTab as CompanyTab : 'clients';

  const subscriptionPlanStrings: { [key: string]: string } = {
    [SercomEnums.SubscriptionPlan.Solo]: t('Solo'),
    [SercomEnums.SubscriptionPlan.Squad]: t('Squad'),
    [SercomEnums.SubscriptionPlan.Enterprise]: t('Enterprise'),
  };
  const getSubscriptionPlanEnumOptions = () =>
    Object.keys(subscriptionPlanStrings).map((key) => ({
      label: subscriptionPlanStrings[key],
      value: parseInt(key),
    }));
  const subscriptionPlanValueAccessor = (field: string, data: any) => subscriptionPlanStrings[data[field]];

  const subscriptionFrequencyStrings: { [key: string]: string } = {
    [SercomEnums.SubscriptionFrequency.Month]: t('Month'),
    [SercomEnums.SubscriptionFrequency.Year]: t('Year'),
  };
  const getSubscriptionFrequencyEnumOptions = () =>
    Object.keys(subscriptionFrequencyStrings).map((key) => ({
      label: subscriptionFrequencyStrings[key],
      value: parseInt(key),
    }));
  const subscriptionFrequencyValueAccessor = (field: string, data: any) => subscriptionFrequencyStrings[data[field]];

  const companyStatusOptions = [
    { label: t('StatusActive'), value: 0 },
    { label: t('StatusExpired'), value: 1 },
  ];

  useEffect(() => {
    async function init() {
      await refreshKpi();
    }
    init();
    const refreshCompanyGrid = () => companyGridRef.current?.refresh?.();
    window.addEventListener('sercom-company-created', refreshCompanyGrid);
    return () => window.removeEventListener('sercom-company-created', refreshCompanyGrid);
  }, []);

  useEffect(() => {
    let isCurrent = true;
    setDataSource(undefined);

    AdminService.getCompaniesDatasource(activeTab).then((dataManager) => {
      if (isCurrent) {
        setDataSource(dataManager);
      }
    });

    return () => (isCurrent = false);
  }, [activeTab]);

  const changeCompanyTab = (_event: React.SyntheticEvent, value: CompanyTab) => {
    setSearchParams(value === 'clients' ? {} : { view: value });
  };

  const refreshKpi = async () => {
    const kpisData = await AdminService.getKpis();
    if (kpisData.isSuccess && kpisData.data.isSuccess) {
      console.log('kpisData.data:', kpisData.data);
      setKpisData(kpisData.data.data);
    }
  };

  // useEffect(() => {
  //   if (!item.id) {
  //     const defaultTax = taxes.findLast((x) => x.isDefault == true);
  //     if (defaultTax) {
  //       setItem({ ...item, taxId: defaultTax.id });
  //     }
  //   }
  // }, [affiliates]);

  const openSubscriptionPopup = (id?: string) => {
    popupManager.open(SubscriptionPopup, {
      id: id,
      action: () => {
        companyGridRef.current?.refresh?.();
        refreshKpi();
      },
      buttonText: t('Update'),
      onClose: () => {},
    });
  };

  const openCloseCompany = async (id?: string) => {
    if (id) {
      const result = await AdminService.closeCompany(id);
      if (result.isSuccess && result.data) {
        if (result.data.errorCode && result.data.errorCode != SercomEnums.ErrorCode.None) {
          snackbarHelper.showErrorFromCode(result.data.errorCode, t);
        } else {
          snackbarHelper.showSuccess(t('CompanyClosedSuccessfully'));
          companyGridRef.current?.refresh?.();
        }
      } else {
        snackbarHelper.showErrorFromCode(SercomEnums.ErrorCode.ServerError, t);
      }
    }
  };

  const openClearQuickBooksLinksPopup = (id?: string, companyName?: string) => {
    popupManager.open(ClearQuickBooksLinksPopup, {
      id,
      data: { companyName },
      action: () => companyGridRef.current?.refresh?.(),
      onClose: () => {},
    });
  };

  const openCompanyTimersPopup = (companyId: string, companyName: string) => {
    popupManager.open(CompanyTimersPopup, {
      companyId,
      companyName,
      action: () => companyGridRef.current?.refresh?.(),
      onClose: () => {},
    });
  };

  const rowStatusTemplate = (prop: any) => {
    const { ExpirationDate, IsDemo, IsTrial, AffiliateId } = prop;
    const isExpired = moment(ExpirationDate).isBefore(moment(), 'day');
    const expiresToday = moment(ExpirationDate).isSame(moment(), 'day');
    const status = IsDemo
      ? { label: t('DemoInternal'), color: '#9C5F10', surface: '#FFF4DC' }
      : isExpired
      ? { label: 'Expired', color: '#B74F49', surface: '#FCECEA' }
      : expiresToday
      ? { label: 'Expires today', color: '#A97020', surface: '#FCF3E2' }
      : IsTrial
      ? { label: 'Trial', color: '#66589B', surface: '#F0EEFA' }
      : { label: 'Active', color: '#167A6C', surface: '#E7F6F2' };

    return <Box className="company-status-chip" title={AffiliateId ? 'Affiliate account' : undefined} style={{ '--status-color': status.color } as React.CSSProperties} sx={{ display: 'inline-flex', alignItems: 'center', px: 1, py: .4, borderRadius: 99, bgcolor: status.surface, fontSize: 11, fontWeight: 750, lineHeight: 1, whiteSpace: 'nowrap' }}>{status.label}</Box>;
  };

  const statusTemplate: any = rowStatusTemplate;

  const demoTemplate = (prop: any) => prop.IsDemo ? (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', px: 1, py: .4, borderRadius: 99, bgcolor: '#FFF4DC', color: '#9C5F10', fontSize: 11, fontWeight: 750, lineHeight: 1, whiteSpace: 'nowrap' }}>
      {t('Internal')}
    </Box>
  ) : null;

  const getTrialActivity = (prop: any) => {
    const entityCounts = [prop.RequestsCount, prop.QuotesCount, prop.JobsCount, prop.InvoicesCount];
    return entityCounts.reduce((total, count) => total + (Number(count) || 0), 0);
  };

  const trialActivityTemplate = (prop: any) => {
    if (!prop.IsTrial || prop.IsDemo) {
      return '—';
    }

    const activity = getTrialActivity(prop);
    const breakdown = t('TrialActivityBreakdown', {
      requests: prop.RequestsCount ?? 0,
      quotes: prop.QuotesCount ?? 0,
      jobs: prop.JobsCount ?? 0,
      invoices: prop.InvoicesCount ?? 0,
    });
    return <Tooltip title={breakdown}><Box component="span" sx={{ fontWeight: 700 }}>{activity}</Box></Tooltip>;
  };

  const trialLeadTemplate = (prop: any) => {
    if (!prop.IsTrial || prop.IsDemo) {
      return '—';
    }

    const activity = getTrialActivity(prop);
    const entityCounts = [prop.RequestsCount, prop.QuotesCount, prop.JobsCount, prop.InvoicesCount];
    const usedEntityTypes = entityCounts.filter((count) => (Number(count) || 0) > 0).length;
    const hasRecentActivity = prop.LastActivityDate &&
      moment(prop.LastActivityDate).isSameOrAfter(moment().subtract(7, 'days'));
    const hasHotActivity = prop.LastActivityDate &&
      moment(prop.LastActivityDate).isSameOrAfter(moment().subtract(3, 'days'));
    const isHot = (hasHotActivity && activity >= 3) || (activity >= 7 && usedEntityTypes >= 2);
    const lead = isHot
      ? { label: t('Hot'), color: '#167A6C', surface: '#E7F6F2' }
      : activity > 0 && hasRecentActivity
      ? { label: t('Warm'), color: '#A97020', surface: '#FCF3E2' }
      : { label: t('Cold'), color: '#71809C', surface: '#F1F4F8' };

    return <Box sx={{ display: 'inline-flex', alignItems: 'center', px: 1, py: .4, borderRadius: 99, bgcolor: lead.surface, color: lead.color, fontSize: 11, fontWeight: 750, lineHeight: 1, whiteSpace: 'nowrap' }}>{lead.label}</Box>;
  };

  const rowMenuTemplate = (prop: any) => {
    const items: SercomTypes.SouvlakiMenuItemProps[] = [];
    items.push({
      text: t('Edit'),
      icon: <EditOutlined sx={{ width: 20, mr: 2 }} />,
      action: () => openSubscriptionPopup(prop.Id),
    });

    if (prop.HasQuickBooksIntegration) {
      items.push({
        text: t('ClearQuickBooksLinksMenu'),
        icon: <LinkOffOutlined sx={{ width: 20, mr: 2 }} />,
        action: () => openClearQuickBooksLinksPopup(prop.Id, prop.Name),
      });
    }

    if (moment(prop.ExpirationDate).isBefore(moment())) {
      items.push({
        text: t('Close'),
        icon: <DeleteOutline sx={{ width: 20, mr: 2 }} />,
        action: () => openCloseCompany(prop.Id),
      });
    }

    return <SercomMenu items={items} />;
  };

  const menuColumn: any = rowMenuTemplate;

  const timersTemplate = (prop: any) => {
    const count = prop.TimersCount ?? 0;
    if (count === 0) {
      return null;
    }

    return <Tooltip title="View running timers"><IconButton onClick={() => openCompanyTimersPopup(prop.Id, prop.Name)} size="small" sx={{ gap: .5, color: '#167A6C' }}><TimerOutlined fontSize="small" /><Typography component="span" sx={{ color: 'inherit', fontSize: 12, fontWeight: 750 }}>{count}</Typography></IconButton></Tooltip>;
  };

  const onRecordClick = (args: RecordClickEventArgs) => {
    if (!args.cell?.classList?.contains('e-templatecell')) {
      const rowData = args.rowData as { Id?: string } | undefined;
      const id = rowData?.Id;
      openSubscriptionPopup(id);
    }
  };

  const columns = [
    { field: 'Id', type: 'string', headerText: 'Id', isPrimaryKey: true, visible: false, showInColumnChooser: false },
    { field: 'Status', type: 'number', template: statusTemplate, headerText: t('Status'), enumFilter: companyStatusOptions, visible: true, allowSearch: false, width: 110 },
    { field: 'IsDemo', type: 'boolean', template: demoTemplate, headerText: t('Demo'), visible: true, allowFiltering: false, allowSearch: false, width: 95 },
    { template: trialActivityTemplate, headerText: t('TrialActivity'), visible: true, allowFiltering: false, allowSorting: false, allowSearch: false, width: 105 },
    { field: 'TrialLead', template: trialLeadTemplate, headerText: t('TrialLead'), visible: true, allowFiltering: false, allowSearch: false, width: 95 },
    { field: 'Name', type: 'string', headerText: t('Name'), visible: true, allowSearch: true },
    { field: 'FullName', type: 'string', headerText: t('UserName'), visible: true, allowSearch: true },
    { field: 'Email', type: 'string', headerText: t('Email'), visible: true, allowSearch: true },
    { field: 'Phone', type: 'string', headerText: t('Phone'), visible: false, allowSearch: true },
    {
      field: 'CreationDate',
      type: 'date',
      headerText: t('CreationDate'),
      format: { type: 'date', format: SercomConstants.DateFormat.SYNCFUSION_ISO },
      visible: true,
    },
    {
      field: 'LastActivityDate',
      type: 'date',
      headerText: t('LastLogin'),
      format: { type: 'date', format: SercomConstants.DateTimeFormat.SYNCFUSION_ISO },
      visible: true,
    },
    {
      field: 'SubscriptionPlan',
      type: 'number',
      headerText: t('Plan'),
      valueAccessor: subscriptionPlanValueAccessor,
      enumFilter: getSubscriptionPlanEnumOptions(),
      visible: true,
    },
    {
      field: 'SubscriptionFrequency',
      type: 'number',
      headerText: t('Frequency'),
      valueAccessor: subscriptionFrequencyValueAccessor,
      enumFilter: getSubscriptionFrequencyEnumOptions(),
      visible: true,
    },
    {
      field: 'CountMembers',
      type: 'number',
      headerText: t('Members'),
      visible: true,
    },
    {
      field: 'MaxMembers',
      type: 'number',
      headerText: t('MaxMembers'),
      visible: true,
    },
    {
      field: 'ExpirationDate',
      type: 'date',
      headerText: t('ExpirationDate'),
      format: { type: 'date', format: SercomConstants.DateFormat.SYNCFUSION_ISO },
      visible: true,
    },
    {
      field: 'ClientsCount',
      type: 'number',
      headerText: t('Clients'),
      visible: false,
    },
    {
      field: 'RequestsCount',
      type: 'number',
      headerText: t('Requests'),
      visible: false,
    },
    {
      field: 'QuotesCount',
      type: 'number',
      headerText: t('Quotes'),
      visible: false,
    },
    {
      field: 'InvoicesCount',
      type: 'number',
      headerText: t('Invoices'),
      visible: false,
    },
    {
      field: 'JobsCount',
      type: 'number',
      headerText: t('Jobs'),
      visible: false,
    },
    {
      field: 'TimeEntriesCount',
      type: 'number',
      headerText: t('TimeEntries'),
      visible: false,
    },
    {
      field: 'TimersCount',
      type: 'number',
      headerText: t('Timers'),
      template: timersTemplate,
      visible: true,
      allowSearch: false,
      width: 82,
    },
    { field: 'Address', type: 'string', headerText: t('Address'), visible: false, allowSearch: true },
    { field: 'Url', type: 'string', headerText: t('Url'), visible: false, allowSearch: false },
    { field: 'Note', type: 'string', headerText: t('Note'), visible: false, allowSearch: true },
    { headerText: '', template: menuColumn, width: 20, showInColumnChooser: false },
  ];
  const sortSettings: SortSettingsModel = { columns: [{ field: 'LastActivityDate', direction: 'Descending' }] };

  const getKpiData = (
    title: string, current: number, target: number, icon: React.ReactNode, iconColor: string, addDollarSign?: boolean
  ) => {
    const ration = current / target;
    const dollarSign = addDollarSign ? '$' : '';

    return (
      <Paper elevation={0} sx={{ minWidth: 190, height: 148, backgroundColor: '#FFFFFF', border: '1px solid #E8ECF4', borderRadius: 3, px: 2.5, py: 2.25, flex: 1 }}>
        <Stack direction="column" spacing={0} sx={{ height: '100%' }}><Stack direction="row" justifyContent="space-between" alignItems="flex-start"><Typography sx={{ color: '#71809C', fontWeight: 650, fontSize: 12 }}>{t(title)}</Typography><Box sx={{ width: 36, height: 36, display: 'grid', placeItems: 'center', borderRadius: 2, bgcolor: `${iconColor}16`, color: iconColor }}>{icon}</Box></Stack>
          <Typography
            variant="subtitle1"
            sx={{ color: '#1D2A46', fontWeight: 750, fontSize: 36, lineHeight: 1, letterSpacing: '-1.4px', mt: 1.25 }}
          >
            {`${dollarSign}${SercomHelper.getFormattedNumber(current)}`}
          </Typography>
          <Stack direction="row" justifyContent="flex-end" spacing={1} sx={{ mt: 'auto' }}>
            <Typography variant="caption" sx={{ fontSize: 12, color: ration < 0.5 ? '#D84747' : ration < 0.75 ? '#C67A14' : '#13966B', fontWeight: 650 }}>
              {`${dollarSign}${SercomHelper.getFormattedNumber(target)} (${Math.round((current / target) * 100)}%)`}
            </Typography>
          </Stack>
        </Stack>
      </Paper>
    );
  };

  const getNextPayment = (name: string, amount: number, date: Date) => {
    return (
      <Paper elevation={0} sx={{ minWidth: 190, height: 148, backgroundColor: '#FFFFFF', border: '1px solid #E8ECF4', borderRadius: 3, px: 2.5, py: 2.25, flex: 1 }}>
        <Stack direction="column" spacing={0}><Stack direction="row" justifyContent="space-between" alignItems="flex-start"><Typography sx={{ color: '#71809C', fontWeight: 650, fontSize: 12 }}>Next Payment</Typography><Box sx={{ width: 36, height: 36, display: 'grid', placeItems: 'center', borderRadius: 2, bgcolor: '#E7F6F2', color: '#1E9B88' }}><CalendarMonthOutlined fontSize="small" /></Box></Stack>
          <Typography variant="subtitle1" sx={{ color: '#1D2A46', fontWeight: 750, fontSize: 36, lineHeight: 1, letterSpacing: '-1.4px', mt: 1.25 }}>
            {`$${SercomHelper.getFormattedNumber(amount)}`}
          </Typography>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ width: 'calc(100% + 40px)', mx: -2.5, mt: 1.25, px: 2.5, py: 1, borderTop: '1px solid #EEF1F6', gap: 1 }}><Typography variant="caption" sx={{ fontSize: 12, color: '#566783', lineHeight: 1.35, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name}</Typography><Typography variant="caption" sx={{ flexShrink: 0, fontSize: 12, color: '#8A98B0', lineHeight: 1.35 }}>{moment(date).format(SercomConstants.DateFormat.MOMENT_DEFAULT)}</Typography></Stack>
        </Stack>
      </Paper>
    );
  };

  const getKpisData = () => {
    return (
      kpisData && (
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ alignSelf: 'end', width: '100%' }}>
          {getKpiData('MRR', kpisData.currentMRR, kpisData.targetMRR, <PaidOutlined fontSize="small" />, '#1E9B88', true)}
          {getKpiData('ARR', kpisData.currentARR, kpisData.targetARR, <AccountBalanceWalletOutlined fontSize="small" />, '#6F63B5', true)}
          {getKpiData('Clients', kpisData.currentClientsCount, kpisData.targetClientsCount, <GroupsOutlined fontSize="small" />, '#D38A34')}
          {getNextPayment(kpisData.nextPaymentName, kpisData.nextPaymentAmount, kpisData.nextPaymentDate)}
        </Stack>
      )
    );
  };

  return dataSource ? (
    dataSource && (
      <Box sx={{ display: 'flex', flexDirection: 'column', pb: { xs: 10, md: 0 } }}>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', xl: 'row' }, gap: 2, justifyContent: 'flex-end', mb: 2.5 }}>
          {getKpisData()}
        </Box>
        <Paper elevation={0} sx={{ border: '1px solid #E8ECF4', borderRadius: 3, bgcolor: '#fff', overflow: 'hidden' }}>
          <Tabs value={activeTab} onChange={changeCompanyTab} aria-label="Company account type" sx={{ px: { xs: 1, md: 2 }, borderBottom: '1px solid #E8ECF4', minHeight: 54, '& .MuiTabs-indicator': { bgcolor: '#1E9B88', height: 3 }, '& .MuiTab-root': { minHeight: 54, px: { xs: 1.5, md: 2.5 }, textTransform: 'none', color: '#71809C', fontSize: 14, fontWeight: 650 }, '& .Mui-selected': { color: '#167A6C !important' } }}>
            {companyTabs.map((tab) => <Tab key={tab.value} value={tab.value} label={t(tab.label)} />)}
          </Tabs>
          <Box sx={{ p: { xs: 2, md: 3 } }}><SercomGrid
          key={activeTab}
          gridRef={companyGridRef}
          dataSource={dataSource}
          columns={columns}
          gridCustomizationType={gridCustomizationTypes[activeTab]}
          recordClick={onRecordClick}
          sortSettings={sortSettings}
          /></Box>
        </Paper>
      </Box>
    )
  ) : (
    <div></div>
  );
};

export default CompaniesPage;
